import { useEffect, useState } from 'react';
import { Shield, Cloud, Lock, Award, Code } from 'lucide-react';
import { useAuth } from '../context/AuthProvider';
import { useNavigate } from 'react-router-dom';


const SignIn = () => {
    const [isHovered, setIsHovered] = useState(false);
    const { keycloak, isAuthenticated, isInitialized } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isInitialized && isAuthenticated) {
            navigate('/dashboard');
        }
    }, [isInitialized, isAuthenticated, navigate]);

    if (!isInitialized || isAuthenticated) {
        return (
            <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-3">
                <img src="/gif3.gif" alt="Loading..." className="w-16 h-16" />
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest animate-pulse">
                    Authenticating...
                </p>
            </div>
        );
    }

    const handleSignIn = () => {
        keycloak.login({ redirectUri: window.location.origin + '/dashboard' });
    };
    const securityFeatures = [
        {
            icon: <Lock className="w-5 h-5" />,
            title: "External Asset Scanning",
            description:
                "Continuous perimeter scans across Internet‑facing cameras, switches, and servers with prioritized vulnerability insights.",
            gradient: "from-red-500/20 to-pink-500/20",
            iconGradient: "from-red-500 to-pink-600",
        },
        {
            icon: <Cloud className="w-5 h-5" />,
            title: "Attack Surface Monitoring",
            description:
                "Automatic discovery of exposed IPs and services, real-time status tracking.",
            gradient: "from-blue-500/20 to-cyan-500/20",
            iconGradient: "from-blue-500 to-cyan-600",
        },
        {
            icon: <Shield className="w-5 h-5" />,
            title: "Asset Health Monitoring",
            description:
                "Real-time status and performance checks for cameras, switches, and servers to detect outages, high load, and storage issues before they impact uptime.",
            gradient: "from-green-500/20 to-emerald-500/20",
            iconGradient: "from-green-500 to-emerald-600",
        },

        {
            icon: <Code className="w-5 h-5" />,
            title: "Service & Port Intelligence",
            description:
                "Deep inspection of open ports and services to detect misconfigurations, exposed management interfaces, and weak defaults.",
            gradient: "from-purple-500/20 to-indigo-500/20",
            iconGradient: "from-purple-500 to-indigo-600",
        },
    ];


    const platformStats = [
        { label: "Cloud Platforms", value: "3+", icon: <Cloud className="w-4 h-4" /> },
        { label: "Security Checks", value: "500+", icon: <Shield className="w-4 h-4" /> },
        { label: "Compliance Standards", value: "12+", icon: <Award className="w-4 h-4" /> },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden flex flex-col lg:flex-row">
            {/* Background Effects */}

            {/* Left Section */}
            <div className="flex-1 flex flex-col justify-center items-center px-6 lg:px-10 py-10 relative z-10">
                <div className="w-full max-w-md space-y-2">
                    <div className="text-center">
                        <div
                            className="mx-auto w-24 h-24 rounded-3xl flex items-center justify-center  hover:scale-105 transition-all"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                        >
                            <img src='/gif3.gif' className={`w-24 h-24 text-white transition-transform duration-500 ${isHovered ? 'rotate-12 scale-110' : ''}`} />
                        </div>
                        <h1 className="mt-2 text-5xl font-black  bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                            OVUL
                        </h1>
                        <p className="mt-2 text-slate-400 text-base">Advanced Security Platform</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3 my-6">
                        {platformStats.map((stat, index) => (
                            <div key={index} className="group bg-slate-800/60 p-3 rounded-xl border border-slate-700 hover:border-orange-500 transition-all">
                                <div className="flex items-center gap-1 mb-1 text-orange-400 group-hover:text-orange-300">
                                    {stat.icon}
                                    <span className="text-xs text-slate-400">{stat.label}</span>
                                </div>
                                <div className="text-xl font-bold text-white group-hover:text-orange-300">
                                    {stat.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={handleSignIn}
                        className="group w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:scale-105 transition-all"
                    >
                        <span className="relative z-10 text-2xl">Sign In</span>
                    </button>

                    <div className="text-center mt-4 text-xs text-slate-500 space-y-1">
                        <p>Enterprise-grade security monitoring</p>
                        <div className="flex justify-center gap-3 text-slate-600">
                            <span className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span>Systems Online</span>
                            </span>
                            <span className="flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                <span>Secure Access</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Section */}
            <div className="flex-1 px-6 py-10 bg-slate-800/30 backdrop-blur-lg border-l border-slate-700 relative z-10 overflow-y-auto">
                <div className="max-w-lg mx-auto">
                    <h2 className="text-3xl font-black text-white mb-4">
                        Complete Security <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Coverage</span>
                    </h2>
                    <p className="text-slate-300 text-sm mb-6">
                        Comprehensive external scanning and cloud security compliance across all major platforms.
                        Protect your infrastructure with enterprise-grade security monitoring and real-time threat detection.
                    </p>

                    <div className="space-y-4">
                        {securityFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className={`group flex items-start gap-4 p-4 rounded-xl bg-gradient-to-br ${feature.gradient} backdrop-blur-xl border border-slate-700 hover:border-orange-500 hover:shadow-lg hover:scale-[1.02] transition-all`}
                            >
                                <div className={`p-3 bg-gradient-to-br ${feature.iconGradient} rounded-lg shadow-lg group-hover:scale-105 transition-transform`}>
                                    <div className="text-white">
                                        {feature.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-white font-semibold text-base mb-1 group-hover:text-orange-300">
                                        {feature.title}
                                    </h3>
                                    <p className="text-slate-300 text-sm group-hover:text-slate-200">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignIn;
